/**
 * Thin wrapper around the backend TMDB proxy.
 *
 * v1.2.12 redesign: client-side caching (localStorage / IndexedDB) was
 * removed because a 50k-movie playlist reliably blew up the WebView
 * heap with "Data cannot be cloned, out of memory" the moment the
 * scanner tried to persist a fresh result.
 *
 * The cache now lives server-side (Mongo `metadata_cache` with a 30-day
 * TTL), so every device launch is stateless — `getCachedTmdb` simply
 * fires a request and the backend either hits its cache or falls
 * through to TMDB. We keep a small in-memory de-dup map per session so
 * a busy Browse view doesn't fire the same lookup twice.
 *
 * When a lookup returns no result, we push the (kind, name, year)
 * onto a per-session "missing" set and batch-POST it to
 * `/api/player/missing-metadata` so the admin can see the long-tail
 * list and backfill manually.
 */
import { fetchTmdbMovie, fetchTmdbTv, API, playerAxios } from "./playerApi";

function keyFor(kind, name, year) {
  return `${kind}|${(name || "").trim().toLowerCase()}|${year || ""}`;
}

// In-flight + recently-resolved promises. Bounded so a 50k-movie scan
// can't keep references to every payload in memory.
const _inflight = new Map(); // key → Promise
const _resolved = new Map(); // key → payload (capped at 200)
const RESOLVED_CAP = 200;

// v1.1.0 — Persist NEGATIVE lookups (misses) across app restarts.
// Positive hits are refetched (rare + payloads change over time), but
// misses re-hammering TMDB every launch was the #1 cause of the
// "Series category loads slow" complaint. We keep ~2 000 recent
// misses in localStorage keyed by `kind|name|year`.
const NEG_LS_KEY = "vupp_tmdb_neg_v1";
const NEG_CAP = 2000;
const _neg = (() => {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(NEG_LS_KEY) : null;
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
})();
let _negFlushTimer = null;
function _rememberNegative(key) {
  _neg.add(key);
  if (_neg.size > NEG_CAP) {
    // Drop oldest — Set preserves insertion order.
    const first = _neg.values().next().value;
    _neg.delete(first);
  }
  if (_negFlushTimer) return;
  _negFlushTimer = setTimeout(() => {
    _negFlushTimer = null;
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(NEG_LS_KEY, JSON.stringify(Array.from(_neg)));
      }
    } catch { /* quota — ignore */ }
  }, 2000);
}

// v1.1.0 — Concurrency limiter. When Browse opens a big Series
// category the WebView mounts hundreds of PosterImages at once and
// each one that lacks a primary cover URL fires getCachedTmdb().
// Without a limit that stacks 200+ parallel fetches, saturates the
// backend Xtream/TMDB proxy, and freezes the UI for 3-5 seconds.
// We cap in-flight requests at MAX_CONCURRENT and drain a FIFO queue.
const MAX_CONCURRENT = 4;
let _running = 0;
const _waiters = [];
function _acquire() {
  return new Promise((resolve) => {
    if (_running < MAX_CONCURRENT) {
      _running++;
      resolve();
    } else {
      _waiters.push(resolve);
    }
  });
}
function _release() {
  const next = _waiters.shift();
  if (next) next();
  else _running--;
}

function rememberResolved(key, payload) {
  _resolved.set(key, payload);
  if (_resolved.size > RESOLVED_CAP) {
    // Drop oldest insertion (Map preserves insertion order).
    const firstKey = _resolved.keys().next().value;
    _resolved.delete(firstKey);
  }
}

// Missing-item reporter — queues up unmatched (kind, name, year)
// tuples and flushes ~every 10s to `/api/player/missing-metadata`.
const _missingQueue = []; // [{kind, name, year, tmdbId}]
const _missingSeen = new Set();
let _flushTimer = null;

function queueMissing({ kind, name, year, tmdbId }) {
  const k = keyFor(kind, name, year);
  if (_missingSeen.has(k)) return;
  _missingSeen.add(k);
  _missingQueue.push({ kind, name, year: year ? Number(year) : null, tmdb_id: tmdbId || null });
  if (!_flushTimer) {
    _flushTimer = setTimeout(flushMissing, 10_000);
  }
}

async function flushMissing() {
  _flushTimer = null;
  if (_missingQueue.length === 0) return;
  // Cap per flush so a sudden 5000-item scan only ships in 1000-item chunks.
  const batch = _missingQueue.splice(0, 1000);
  try {
    await playerAxios.post("/player/missing-metadata", { items: batch });
  } catch {
    // On failure put the items back so we retry on next launch.
    _missingQueue.unshift(...batch);
  }
  // If anything more accumulated while we were in-flight, schedule another flush.
  if (_missingQueue.length > 0) {
    _flushTimer = setTimeout(flushMissing, 30_000);
  }
}

async function searchOne(kind, name, year) {
  try {
    const url = `${API}/tmdb/search/${kind === "series" ? "tv" : "movie"}?q=${encodeURIComponent(name)}${year ? `&year=${year}` : ""}`;
    const r = await fetch(url, { headers: { accept: "application/json" } });
    if (!r.ok) return null;
    const data = await r.json();
    // Backend returns `{results: []}` when TMDB has nothing — treat as miss.
    if (data && Array.isArray(data.results) && data.results.length === 0) return null;
    return data;
  } catch {
    return null;
  }
}

/**
 * Resolve TMDB metadata for a movie/series. Backend handles caching.
 * Returns the payload or null. Misses are reported to admin in batches.
 */
export async function getCachedTmdb(kind, name, year, opts = {}) {
  const k = keyFor(kind, name, year);
  if (_resolved.has(k)) return _resolved.get(k);
  // v1.1.0 — Fast-path negative cache. Skip everything if we already
  // know TMDB has nothing for this title.
  if (_neg.has(k)) return null;
  if (_inflight.has(k)) return _inflight.get(k);

  const tmdbId = opts.tmdbId || null;

  const p = (async () => {
    // v1.1.0 — respect the global concurrency cap so a fresh Series
    // category opening doesn't fire 300+ TMDB calls in parallel.
    await _acquire();
    try {
      let data = null;
      if (tmdbId) {
        data = kind === "series" ? await fetchTmdbTv(tmdbId) : await fetchTmdbMovie(tmdbId);
      }
      if (!data?.plot && name) {
        data = await searchOne(kind, name, year);
      }
      if (data?.plot) {
        rememberResolved(k, data);
        return data;
      }
      // Miss — tell the admin so they can backfill, AND persist a
      // negative cache entry so the next launch doesn't re-ask.
      if (name) queueMissing({ kind, name, year, tmdbId });
      rememberResolved(k, null);
      _rememberNegative(k);
      return null;
    } finally {
      _release();
    }
  })();

  _inflight.set(k, p);
  try {
    return await p;
  } finally {
    _inflight.delete(k);
  }
}

/**
 * Build the list of items in `channels` that don't already have rich
 * metadata, and immediately ship the entire list to the admin's
 * missing-metadata backlog. Used by the Settings "Report missing
 * metadata" button — no more on-device TMDB hammering.
 */
export async function reportMissingMetadata(channels, onProgress) {
  const seen = new Set();
  const items = [];
  for (const ch of channels || []) {
    if (ch.content_type === "movie" && !ch.tmdb_id) {
      const k = keyFor("movie", ch.name, ch.year);
      if (seen.has(k)) continue;
      seen.add(k);
      items.push({
        kind: "movie",
        name: ch.name,
        year: ch.year ? Number(ch.year) : null,
        tmdb_id: null,
      });
    } else if (ch.content_type === "series" && !ch.tmdb_id) {
      const name = ch.series_name || ch.name;
      const k = keyFor("series", name, ch.year);
      if (seen.has(k)) continue;
      seen.add(k);
      items.push({
        kind: "series",
        name,
        year: ch.year ? Number(ch.year) : null,
        tmdb_id: null,
      });
    }
  }
  if (items.length === 0) {
    onProgress?.({ done: 0, total: 0, reported: 0 });
    return { reported: 0, total: 0 };
  }
  // Ship in 500-item chunks so we don't post a giant body in one go.
  const CHUNK = 500;
  let reported = 0;
  let lastError = null;
  for (let i = 0; i < items.length; i += CHUNK) {
    const chunk = items.slice(i, i + CHUNK);
    try {
      const r = await playerAxios.post("/player/missing-metadata", { items: chunk });
      // Trust the server's `stored` count when present so the UI shows
      // the actual de-duplicated number that landed in the backlog.
      reported += (r.data && typeof r.data.stored === "number")
        ? r.data.stored
        : chunk.length;
    } catch (err) {
      lastError = err;
      // continue on network failure — the next launch can retry
    }
    onProgress?.({ done: Math.min(i + CHUNK, items.length), total: items.length, reported });
  }
  return { reported, total: items.length, error: lastError };
}

/** Legacy compat — the old localStorage-cache APIs other modules still call. */
export function clearTmdbCache() {
  _resolved.clear();
  _inflight.clear();
  try {
    localStorage.removeItem("zapp_tmdb_lookup_v1");
  } catch {
    /* no-op */
  }
}

export function getCacheStats() {
  return { entries: _resolved.size };
}

/**
 * v1.1.3 — Synchronous cache probe. Returns the cached TMDB payload
 * or `null` without triggering any network. Used by PosterImage to
 * seed its initial `currentSrc` on mount so tiles that were already
 * resolved once don't flash a placeholder for 150 ms before the
 * async lookup completes.
 */
export function peekCachedTmdb(kind, name, year) {
  const k = keyFor(kind, name, year);
  if (_resolved.has(k)) return _resolved.get(k);
  return null;
}
